import React from "react";
import PropTypes from "prop-types";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import i18n from "../../../i18n";
import { Box, Button, Chip, IconButton, Tooltip, Typography } from "@mui/joy";

const HEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

function hexToRgb(hex) {
  if (!hex || !HEX.test(hex)) return null;
  let h = hex.slice(1);
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * Normalize API / user color to #RRGGBB or null.
 */
export function normalizeTagColor(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  if (!s) return null;
  const withHash = s.startsWith("#") ? s : `#${s}`;
  if (!HEX.test(withHash)) return null;
  let h = withHash.slice(1);
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  return `#${h}`.toUpperCase();
}

/** Black on light backgrounds, white on dark (relative luminance). */
export function pickContrastText(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#000";
  const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  return luminance > 0.55 ? "#000" : "#fff";
}

function rgbCssToRgb(s) {
  const m = String(s).match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (!m) return null;
  return {
    r: Math.min(255, Math.round(Number(m[1]))),
    g: Math.min(255, Math.round(Number(m[2]))),
    b: Math.min(255, Math.round(Number(m[3]))),
  };
}

/** Contrast text for any CSS background string we support (hex or rgb/rgba). */
function pickContrastForFill(fill) {
  if (fill == null || fill === "") return "#000";
  const hex = normalizeTagColor(fill);
  if (hex) return pickContrastText(hex);
  const rgb = rgbCssToRgb(fill);
  if (rgb) {
    const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
    return luminance > 0.55 ? "#000" : "#fff";
  }
  return "#000";
}

/**
 * fill: value for style.backgroundColor (hex or rgb/rgba).
 * Inline styles beat Joy Autocomplete / VariantColorProvider overriding sx bgcolor.
 */
function tagPillColors(color) {
  const hex = normalizeTagColor(color);
  if (hex) {
    return {
      fill: hex,
      fg: pickContrastText(hex),
      neutral: false,
    };
  }
  const raw = color != null ? String(color).trim() : "";
  if (raw && /^rgba?\(/i.test(raw)) {
    return {
      fill: raw,
      fg: pickContrastForFill(raw),
      neutral: false,
    };
  }
  return {
    fill: null,
    fg: "#111",
    neutral: true,
  };
}

/**
 * Selected tags in Autocomplete: DOM `style` so Joy Chip / field wrapper cannot strip colors.
 */
export function getShipmentTagChipInlineStyle(color) {
  const { fill, fg, neutral } = tagPillColors(color);
  if (!neutral && fill != null) {
    return { backgroundColor: fill, color: fg };
  }
  return undefined;
}

/** ChipDelete (close): match label color, no hover fill (Joy plain IconButton adds hover bg). */
const shipmentTagChipDeleteSx = {
  "& .MuiChip-endDecorator": {
    color: "inherit",
  },
  "& .MuiChipDelete-root": {
    color: "inherit",
    "--Icon-color": "currentColor",
    "&:hover": {
      "@media (hover: hover)": {
        bgcolor: "transparent",
        backgroundColor: "transparent",
        color: "inherit",
        boxShadow: "none",
      },
    },
    "&:active": {
      bgcolor: "transparent",
      backgroundColor: "transparent",
    },
  },
  "& .MuiChipDelete-root svg": {
    color: "inherit",
    fill: "currentColor",
  },
};

/**
 * Layout + neutral gray; for colored tags use with getShipmentTagChipInlineStyle on Chip `style`.
 */
export function getShipmentTagChipSx(color) {
  const { fill, neutral } = tagPillColors(color);
  if (!neutral && fill) {
    return {
      "--Chip-radius": "999px",
      fontWeight: 600,
      fontSize: "0.75rem",
      lineHeight: 1.35,
      minHeight: 0,
      maxWidth: "100%",
      border: "none",
      letterSpacing: "0.01em",
      boxShadow:
        "inset 0 0 0 1px rgba(0,0,0,0.06), 0 1px 2px rgba(15, 23, 42, 0.06)",
      "& .MuiChip-label": {
        color: "inherit",
        overflow: "visible",
        textOverflow: "clip",
        whiteSpace: "normal",
        lineHeight: 1.35,
      },
      ...shipmentTagChipDeleteSx,
    };
  }
  return {
    "--Chip-radius": "999px",
    fontWeight: 600,
    fontSize: "0.75rem",
    lineHeight: 1.35,
    minHeight: 0,
    maxWidth: "100%",
    border: "1px solid",
    borderColor: "neutral.outlinedBorder",
    letterSpacing: "0.01em",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    "&&": {
      bgcolor: "var(--joy-palette-neutral-100)",
      backgroundColor: "var(--joy-palette-neutral-100)",
      color: "neutral.800",
    },
    color: "neutral.800",
    "& .MuiChip-label": {
      overflow: "visible",
      textOverflow: "clip",
      whiteSpace: "normal",
      lineHeight: 1.35,
    },
    ...shipmentTagChipDeleteSx,
  };
}

function TagPill({ name, color, fontSize }) {
  const { fill, fg, neutral } = tagPillColors(color);
  const inlineColor =
    !neutral && fill != null
      ? {
          backgroundColor: fill,
          color: fg,
        }
      : undefined;
  return (
    <Box
      component="span"
      title={name}
      style={inlineColor}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        maxWidth: "100%",
        minWidth: 0,
        px: 1.125,
        py: "5px",
        borderRadius: "999px",
        fontSize: fontSize != null ? fontSize : "0.75rem",
        fontWeight: 600,
        lineHeight: 1.35,
        letterSpacing: "0.01em",
        boxSizing: "border-box",
        wordBreak: "break-word",
        ...(neutral
          ? {
              bgcolor: "var(--joy-palette-neutral-100)",
              color: "neutral.800",
              border: "1px solid",
              borderColor: "neutral.outlinedBorder",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
            }
          : {
              boxShadow:
                "inset 0 0 0 1px rgba(0,0,0,0.06), 0 1px 2px rgba(15, 23, 42, 0.06)",
            }),
      }}
    >
      {name}
    </Box>
  );
}

TagPill.propTypes = {
  name: PropTypes.string.isRequired,
  color: PropTypes.string,
  fontSize: PropTypes.string,
};

/** Same pill as in the shipment table — use in dropdowns, filters, etc. */
export { TagPill as ShipmentTagPill };

/**
 * One row in tag autocomplete: color accent + pill + optional color edit + optional usage count.
 * Pass onEditColor for catalog tags so users can change color without selecting the row.
 */
export function ShipmentTagOptionRow({
  name,
  color,
  shipmentCount,
  fontSize = "0.8125rem",
  onEditColor,
  onDelete,
}) {
  const hex = normalizeTagColor(color);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        width: "100%",
        minWidth: 0,
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 4,
          alignSelf: "stretch",
          minHeight: 26,
          borderRadius: "999px",
          flexShrink: 0,
          bgcolor: hex != null ? hex : "neutral.300",
          boxShadow: hex != null ? "inset 0 0 0 1px rgba(0,0,0,0.12)" : "none",
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TagPill name={name} color={color} fontSize={fontSize} />
      </Box>
      {typeof onEditColor === "function" && (
        <Tooltip title={i18n.t("shipmentTag.changeColor", { ns: "ui" })} placement="left" variant="soft" size="sm">
          <Button
            size="sm"
            variant="soft"
            color="neutral"
            aria-label={i18n.t("shipmentTag.ariaChangeColorForTag", { ns: "ui", name })}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onEditColor();
            }}
            sx={{
              flexShrink: 0,
              minHeight: 28,
              py: 0,
              px: 1,
              fontSize: "0.75rem",
              fontWeight: 600,
              borderRadius: "8px",
            }}
          >
            {i18n.t("shipmentTag.change", { ns: "ui" })}
          </Button>
        </Tooltip>
      )}
      {typeof onDelete === "function" && (
        <Tooltip title={i18n.t("shipmentTag.deleteFromCatalog", { ns: "ui" })} placement="left" variant="soft" size="sm">
          <IconButton
            size="sm"
            variant="soft"
            color="danger"
            aria-label={i18n.t("shipmentTag.ariaDeleteTagFromCatalog", { ns: "ui", name })}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete();
            }}
            sx={{ flexShrink: 0 }}
          >
            <DeleteOutlineIcon sx={{ fontSize: "1.15rem" }} />
          </IconButton>
        </Tooltip>
      )}
      {shipmentCount != null && (
        <Tooltip title={i18n.t("shipmentTag.shipmentsCount", { count: shipmentCount, ns: "ui" })} variant="soft" size="sm">
          <Typography
            component="span"
            sx={{
              flexShrink: 0,
              px: 0.85,
              py: 0.2,
              borderRadius: "999px",
              bgcolor: "background.level1",
              color: "neutral.600",
              fontWeight: 700,
              fontSize: "0.68rem",
              lineHeight: 1.5,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {shipmentCount}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
}

ShipmentTagOptionRow.propTypes = {
  name: PropTypes.string.isRequired,
  color: PropTypes.string,
  shipmentCount: PropTypes.number,
  fontSize: PropTypes.string,
  onEditColor: PropTypes.func,
  onDelete: PropTypes.func,
};

/**
 * Read-only tags for DataGrid / lists (Box pills — avoids Joy Chip theme overrides).
 */
export function ShipmentTagChips({ tags, max = 5, size = "sm" }) {
  if (!Array.isArray(tags) || tags.length === 0) {
    return (
      <Typography level="body-sm" sx={{ color: "neutral.400", fontStyle: "italic" }}>
        —
      </Typography>
    );
  }
  const sorted = [...tags].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const visible = sorted.slice(0, max);
  const overflow = sorted.slice(max);
  const fontSize = size === "sm" ? "0.75rem" : size === "lg" ? "0.875rem" : "0.8125rem";
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        alignItems: "center",
        alignContent: "flex-start",
        width: "100%",
        minWidth: 0,
        py: 0.35,
      }}
    >
      {visible.map((tag) => (
        <Tooltip key={tag.id != null ? tag.id : tag.name} title={tag.name} variant="soft" size="sm">
          <Box sx={{ maxWidth: "100%", minWidth: 0 }}>
            <TagPill name={tag.name} color={tag.color} fontSize={fontSize} />
          </Box>
        </Tooltip>
      ))}
      {overflow.length > 0 && (
        <Tooltip title={overflow.map((t) => t.name).join(", ")} variant="soft">
          <Chip
            size={size}
            variant="soft"
            color="neutral"
            sx={{
              flexShrink: 0,
              fontWeight: 700,
              borderRadius: "999px",
              "--Chip-paddingInline": "0.5rem",
            }}
          >
            +{overflow.length}
          </Chip>
        </Tooltip>
      )}
    </Box>
  );
}

ShipmentTagChips.propTypes = {
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      color: PropTypes.string,
    })
  ),
  max: PropTypes.number,
  size: PropTypes.string,
};
